import type {
  RepositoryAnalysis,
  Recommendation,
  Finding,
} from '../../../components/types/analysis.types';

// Pipeline + tool derivation and executive-summary metrics, ported from the
// original HTML report generator (pipeline.ts / helpers.ts) with the HTML
// stripped out. Pure data logic so it can be unit-tested independently of the
// react-pdf renderer.

export interface PipelineStage {
  name: string;
  covered: boolean;
  toolCount: number;
}

export interface StageTool {
  name: string;
  type: string;
}

export type ConnectorKind = 'ok' | 'gap' | 'transition' | 'transition-rev';

const STAGE_CATEGORY_PATTERNS: Array<{ stage: string; patterns: string[] }> = [
  { stage: 'Source', patterns: ['source', 'vcs', 'version-control', 'version_control'] },
  { stage: 'Build', patterns: ['build'] },
  { stage: 'Test', patterns: ['test', 'testing', 'quality'] },
  { stage: 'Package', patterns: ['package', 'packaging', 'artifact', 'registry'] },
  { stage: 'Deploy', patterns: ['deploy', 'deployment', 'cicd', 'ci-cd', 'ci_cd', 'release'] },
];

export const PIPELINE_STAGE_ORDER = ['Source', 'Build', 'Test', 'Package', 'Deploy'] as const;

// Reads currentState (legacy LLM path) with repositoryDetails fallbacks — Iris
// results never populate currentState, so without the fallbacks the appendix
// claimed "None detected" for tools the body listed as strengths.
export function deriveStageTools(data: RepositoryAnalysis): Record<string, StageTool[]> {
  const cs = data.currentState;
  const details = data.repositoryDetails;
  const result: Record<string, StageTool[]> = {
    Source: [],
    Build: [],
    Test: [],
    Package: [],
    Deploy: [],
  };

  for (const lang of data.languages ?? []) {
    const name = typeof lang === 'string' ? lang : lang.name;
    if (name) result.Source.push({ name, type: 'Language' });
  }
  const vcs = cs?.collaboration?.versionControl?.type ?? details?.versionControl?.platform;
  if (vcs) result.Source.push({ name: vcs, type: 'Source Control' });

  (cs?.build?.tools ?? []).forEach((t) => result.Build.push({ name: t, type: 'Build Tool' }));
  if (result.Build.length === 0)
    (details?.keyTechnologies ?? []).forEach((t) =>
      result.Build.push({ name: t, type: 'Framework' }),
    );

  const csFrameworks = (cs?.testing?.frameworks ?? []).map((f) =>
    typeof f === 'string' ? f : f.name,
  );
  const testNames = csFrameworks.length > 0 ? csFrameworks : (details?.testingFrameworks ?? []);
  testNames.forEach((name) => result.Test.push({ name, type: 'Test Framework' }));

  (details?.packageManagers ?? []).forEach((p) =>
    result.Package.push({ name: p, type: 'Package Manager' }),
  );
  (details?.capabilities ?? []).forEach((cap) =>
    result.Package.push({ name: cap, type: 'Capability' }),
  );

  const cicd = cs?.deployment?.cicd?.platform ?? details?.cicdPlatform?.replace(/_/g, ' ');
  if (cicd) result.Deploy.push({ name: cicd, type: 'CI/CD' });

  return result;
}

export function derivePipelineStages(data: RepositoryAnalysis): PipelineStage[] {
  const stageTools = deriveStageTools(data);

  return STAGE_CATEGORY_PATTERNS.map(({ stage, patterns }) => {
    const matchingCats = (data.categories ?? []).filter((c) =>
      patterns.some((p) => c.id.toLowerCase().includes(p) || c.name.toLowerCase().includes(p)),
    );
    const tools = stageTools[stage] ?? [];
    const catCovered = matchingCats.some((c) => c.score > 0 && c.status !== 'not_detected');
    return { name: stage, covered: catCovered || tools.length > 0, toolCount: tools.length };
  });
}

export function connectorKind(prev: PipelineStage, next: PipelineStage): ConnectorKind {
  if (prev.covered && next.covered) return 'ok';
  if (prev.covered && !next.covered) return 'transition';
  if (!prev.covered && next.covered) return 'transition-rev';
  return 'gap';
}

// Metric derivations for the executive summary

export function countTechnologies(data: RepositoryAnalysis): number {
  const stageTools = deriveStageTools(data);
  return Object.values(stageTools).reduce((sum, tools) => sum + tools.length, 0);
}

export function technologyLabel(data: RepositoryAnalysis): string {
  const all = Object.values(deriveStageTools(data))
    .flat()
    .map((t) => t.name);
  if (all.length === 0) return 'None detected';
  if (all.length <= 4) return all.join(', ');
  return `${all.slice(0, 3).join(', ')} +${all.length - 3} more`;
}

export function gapPrioritySummary(gaps: Finding[]): string {
  const count = (levels: string[]) =>
    gaps.filter((g) => levels.includes(g.severity?.toLowerCase() ?? '')).length;
  const high = count(['critical', 'high']);
  const medium = count(['medium']);
  const low = count(['low']);
  const parts: string[] = [];
  if (high > 0) parts.push(`${high} high priority`);
  if (medium > 0) parts.push(`${medium} medium`);
  if (low > 0) parts.push(`${low} low`);
  return parts.join(', ') || 'No gaps';
}

export function recPrioritySummary(recs: Recommendation[]): string {
  const count = (levels: string[]) =>
    recs.filter((r) => levels.includes(r.priority?.toLowerCase() ?? '')).length;
  const high = count(['critical', 'high']);
  const medium = count(['medium']);
  const low = count(['low']);
  const parts: string[] = [];
  if (high > 0) parts.push(`${high} high`);
  if (medium > 0) parts.push(`${medium} medium`);
  if (low > 0) parts.push(`${low} low`);
  return parts.join(', ') || '';
}

export function primaryLanguage(data: RepositoryAnalysis): string {
  const lang = data.languages?.[0];
  if (!lang) return '';
  return typeof lang === 'string' ? lang : lang.name || '';
}

export function languagesLabel(data: RepositoryAnalysis): string {
  return (data.languages ?? [])
    .map((l) => (typeof l === 'string' ? l : l.name || ''))
    .filter(Boolean)
    .join(' / ');
}

export function formatDate(dateStr: string | undefined): string {
  if (!dateStr) return '';
  try {
    return new Date(dateStr).toLocaleDateString('en-US', {
      month: 'long',
      day: 'numeric',
      year: 'numeric',
    });
  } catch {
    return dateStr;
  }
}

export function formatDuration(seconds: number | undefined): string {
  if (!seconds) return '';
  if (seconds < 60) return `${seconds}s`;
  const m = Math.floor(seconds / 60);
  const s = seconds % 60;
  return s > 0 ? `${m}m ${s}s` : `${m}m`;
}

export function recType(title: string): { kind: 'add' | 'replace' | 'fix'; label: string } | null {
  const lower = title.toLowerCase();
  if (lower.includes('add ')) return { kind: 'add', label: 'Add' };
  if (
    lower.startsWith('replace') ||
    lower.startsWith('upgrade') ||
    lower.startsWith('update') ||
    lower.startsWith('migrate')
  )
    return { kind: 'replace', label: 'Replace' };
  if (lower.startsWith('fix') || lower.startsWith('remove') || lower.startsWith('enable'))
    return { kind: 'fix', label: 'Fix' };
  return null;
}

export function isLowFrictionHighROI(rec: Recommendation): boolean {
  const time = (rec.estimatedTime || '').toLowerCase();
  const impact = (rec.impact || '').toLowerCase();
  const isShort = /^\d+\s*min|^<\s*1\s*hour|^30\s*min|^15\s*min/.test(time);
  const isHighImpact = impact.includes('high') || impact.includes('critical');
  return isShort && isHighImpact;
}

export interface SummaryParts {
  repoName: string;
  scoreText: string;
  /** Prose between the (bold) repo name and the (bold) score. */
  afterName: string;
  /** Prose after the (bold) score. */
  afterScore: string;
}

// Splits the executive-summary sentence so the renderer can bold the repo name
// and score while keeping the surrounding prose plain.
export function buildSummaryParts(data: RepositoryAnalysis): SummaryParts {
  const catCount = data.categories?.length ?? 0;
  const strengthCount = data.strengths?.length ?? 0;
  const gapCount = (data.gaps ?? []).filter((g) => g.kind !== 'issue').length;
  const lang = primaryLanguage(data);
  const desc = data.repositoryDetails?.description;

  const lead = desc ? `: ${desc}.` : ` is a ${lang ? `${lang} ` : ''}repository.`;
  const afterName = `${lead} The repository has been evaluated with a development score of `;
  let afterScore = `. This assessment analyzed ${catCount} stage${catCount !== 1 ? 's' : ''} of the software development lifecycle, identifying ${strengthCount} established practice${strengthCount !== 1 ? 's' : ''}`;
  afterScore += gapCount > 0 ? ` and ${gapCount} critical gap${gapCount !== 1 ? 's' : ''}.` : '.';

  return { repoName: data.repositoryName, scoreText: `${data.score}/100`, afterName, afterScore };
}
